import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { OrderFinancialTransaction } from '../types';
import { formatCurrency } from '../util/formatCurrency';

const OrderFinancialTransactions = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [transactions, setTransactions] = useState<OrderFinancialTransaction[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreditFilter, setIsCreditFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(searchParams.get('page') || '1', 10);
    return isNaN(p) || p < 1 ? 1 : p;
  });
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const fetchTransactions = async (
    search: string = '',
    page: number = 1,
    isCredit: string = '',
    startDate: string = '',
    endDate: string = ''
  ) => {
    setIsLoading(true);
    try {
      const query = new URLSearchParams();
      if (search) query.append('customer_search', search);
      if (isCredit) query.append('is_credit', isCredit);
      if (startDate) query.append('start_date', startDate);
      if (endDate) query.append('end_date', endDate);
      query.append('page', String(page));
      query.append('per_page', '10');

      const res = await fetchWithAuth(`/order_financial_transactions?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('jwt')}`
        }
      });

      if (!res.ok) {
        throw new Error('Failed to fetch order financial transactions');
      }

      const data = await res.json();
      setTransactions(data.order_financial_transactions);
      setCurrentPage(data.pagination.current_page);
      setTotalPages(data.pagination.total_pages);
      setTotalCount(data.pagination.total_count);
    } catch (error) {
      console.error('Error fetching order financial transactions:', error);
      setTransactions([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions(searchTerm, currentPage, isCreditFilter, startDate, endDate);
  }, [searchTerm, currentPage, isCreditFilter, startDate, endDate]);

  const resetToFirstPage = () => {
    setSearchParams(prev => { prev.set('page', '1'); return prev; });
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setSearchParams(prev => { prev.set('page', String(page)); return prev; });
    setCurrentPage(page);
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return 'N/A';
    // Append time so a YYYY-MM-DD value is parsed in the local timezone, not UTC
    const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(dateString);
    const date = new Date(isDateOnly ? `${dateString}T00:00:00` : dateString);
    return isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString();
  };

  const formatAmount = (amount: string) => {
    const value = parseFloat(amount);
    if (isNaN(value)) return amount;
    const formatted = formatCurrency(Math.abs(value));
    return value < 0 ? `-${formatted}` : formatted;
  };

  const truncateUuid = (uuid: string | null) => {
    if (!uuid) return '—';
    return uuid.length > 24 ? `${uuid.slice(0, 24)}…` : uuid;
  };

  const getTypeBadge = (isCredit: boolean) => {
    const baseClasses = 'px-2 py-1 text-xs font-medium rounded-full';
    return isCredit
      ? <span className={`${baseClasses} bg-purple-100 text-purple-800`}>Credit</span>
      : <span className={`${baseClasses} bg-gray-100 text-gray-800`}>Cash</span>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Order Financial Transactions</h1>
        <p className="text-gray-600">Browse all order-related financial transactions</p>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
          <div>
            <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-1">
              Customer Search
            </label>
            <input
              type="text"
              id="search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Name or customer code..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="isCredit" className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              id="isCredit"
              value={isCreditFilter}
              onChange={(e) => { setIsCreditFilter(e.target.value); resetToFirstPage(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All</option>
              <option value="true">Credit</option>
              <option value="false">Cash</option>
            </select>
          </div>

          <div>
            <label htmlFor="startDate" className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); resetToFirstPage(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); resetToFirstPage(); }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={resetToFirstPage}
              disabled={isLoading}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Loading...' : 'Search'}
            </button>
          </div>
        </div>

        {totalCount > 0 && (
          <p className="text-sm text-gray-600">
            Found {totalCount} transaction{totalCount !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="hidden md:table-cell px-3 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="hidden md:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order
                </th>
                <th className="hidden lg:table-cell px-2 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  UUID
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    Loading transactions...
                  </td>
                </tr>
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                    No order financial transactions found
                  </td>
                </tr>
              ) : (
                transactions.map((txn) => {
                  const amountValue = parseFloat(txn.amount);
                  const amountClass = !isNaN(amountValue) && amountValue < 0
                    ? 'text-red-600'
                    : 'text-gray-900';
                  const rowClass = txn.is_credit
                    ? 'hover:bg-gray-50 bg-purple-50/40'
                    : 'hover:bg-gray-50';

                  return (
                    <tr key={txn.id} className={rowClass}>
                      <td className="hidden md:table-cell px-3 md:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(txn.date)}
                      </td>
                      <td className="px-2 md:px-6 py-4 text-sm text-gray-900 max-w-xs break-words">
                        {txn.description}
                      </td>
                      <td className="px-2 md:px-6 py-4">
                        {txn.customer_id ? (
                          <Link to={`/customers/${txn.customer_id}`} className="hover:underline">
                            <div className="text-sm font-medium text-gray-900">{txn.customer_name}</div>
                            <div className="text-sm text-gray-500">{txn.customer_code}</div>
                          </Link>
                        ) : (
                          <div>
                            <div className="text-sm font-medium text-gray-900">{txn.customer_name}</div>
                            <div className="text-sm text-gray-500">{txn.customer_code}</div>
                          </div>
                        )}
                      </td>
                      <td className={`px-2 md:px-6 py-4 text-sm whitespace-nowrap ${amountClass}`}>
                        {formatAmount(txn.amount)}
                      </td>
                      <td className="px-2 md:px-6 py-4">
                        {getTypeBadge(txn.is_credit)}
                      </td>
                      <td className="hidden md:table-cell px-2 md:px-6 py-4 text-sm text-gray-500">
                        {txn.order_id ? (
                          <Link to={`/orders/${txn.order_id}`} className="text-blue-600 hover:underline">
                            {txn.order_reference || `#${txn.order_id}`}
                          </Link>
                        ) : '—'}
                      </td>
                      <td className="hidden lg:table-cell px-2 md:px-6 py-4 text-xs text-gray-500 font-mono" title={txn.uuid || undefined}>
                        {truncateUuid(txn.uuid)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage <= 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing page <span className="font-medium">{currentPage}</span> of{' '}
                  <span className="font-medium">{totalPages}</span>
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage <= 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage >= totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderFinancialTransactions;
